import { Component, effect, Input } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { CommonModule, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ActivatedRoute, Router } from '@angular/router';
import { ResetPasswordUsecase } from '../../../data/repositories/auth/usecases/reset-password.usecase';
import { ForgotPasswordUseCase } from '../../../data/repositories/auth/usecases/forgot-password.usecase';
import { LoginUsecase } from '../../../data/repositories/auth/usecases';
import { AuthRepositoryQuery } from '../../../domain/repositories/auth.repository';
import { DialogModule } from 'primeng/dialog';

import { DividerModule } from 'primeng/divider';
import { ERROR_MESSAGES_TOKEN } from '../../shared/injection-tokens/error-messages-token';

type ErrorMessages = Record<
  string,
  Record<string, string | ((error: any) => string)>
>;

const errorMessages: ErrorMessages = {
  password: {
    required: 'Това поле е задължително.',
    minlength: 'Минималната дължина на паролата е 8 символа.',
    pattern:
      'Паролата трябва да е 8–25 символа и да съдържа поне малка и главна буква, цифра и специален символ.',
  },
  password_confirmation: {
    required: 'Това поле е задължително.',
    passwordNotMatching: 'Паролите не съвпадат.',
  },
};

@Component({
  selector: 'otw-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    FormFieldComponent,
    PasswordModule,
    DialogModule,
    DividerModule,
  ],
  providers: [
    {
      provide: ERROR_MESSAGES_TOKEN,
      useValue: errorMessages,
    },
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css',
})
export class ResetPasswordComponent {
  @Input() token!: string;
  email: string = '';
  loading: boolean = false;
  errors: { [key: string]: string } = {};
  errorMessage: string = '';
  resetPasswordForm: FormGroup = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,25}$/
          ),
        ],
      ],
      password_confirmation: ['', Validators.required],
    },
    {
      validators: this.matchingPasswordValidator,
    }
  );
  generalError: string = '';
  hasPasswordResetingFailed: boolean = false;

  confirmVisible: boolean = false;
  resultVisible: boolean = false;
  modalMessage: string = '';
  modalSeverity: 'success' | 'danger' = 'success';

  constructor(
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private resetPasswordUseCase: ResetPasswordUsecase,
    private forgotPasswordUseCase: ForgotPasswordUseCase,
    private loginUseCase: LoginUsecase,
    private authRepoQuery: AuthRepositoryQuery,
    private router: Router
  ) {
    this.email = this.activatedRoute.snapshot.queryParamMap.get('email') || '';
    // this.resetPasswordForm = this.fb.group(
    //   {
    //     password: [
    //       '',
    //       [
    //         Validators.required,
    //         Validators.pattern(
    //           /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,25}$/
    //         ),
    //       ],
    //     ],
    //     password_confirmation: ['', Validators.required],
    //   },
    //   {
    //     validators: this.matchingPasswordValidator,
    //   }
    // );

    effect(() => {
      if (this.authRepoQuery.isLoggedIn()) {
        this.router.navigateByUrl('/user/my-listings');
      }
    });
  }

  matchingPasswordValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('password_confirmation')?.value;
    if (!password || !confirmPassword) return null;

    if (password !== confirmPassword) {
      group
        .get('password_confirmation')
        ?.setErrors({ passwordNotMatching: true });
    } else {
      group.get('password_confirmation')?.setErrors(null);
    }

    return null;
  }

  onSubmit() {
    this.resetPasswordForm.markAllAsTouched();
    if (this.resetPasswordForm.invalid) return;
    this.confirmVisible = true;
  }
  async onConfirmReset() {
    this.confirmVisible = false;
    this.loading = true;
    this.generalError = '';

    const { password, password_confirmation } = this.resetPasswordForm.value;
    const result = await this.resetPasswordUseCase.execute({
      password,
      password_confirmation,
      email: this.email,
      token: this.token,
    });

    this.loading = false;
    if (result.isErr()) {
      this.modalMessage =
        result.getErr()?.message || 'Грешка при зануляване на паролата.';
      this.modalSeverity = 'danger';
      this.resultVisible = true;
      this.hasPasswordResetingFailed = true;
      return;
    }

    this.modalMessage = 'Паролата беше успешно променена!';
    this.modalSeverity = 'success';
    this.resultVisible = true;
    this.hasPasswordResetingFailed = false;

    await this.loginUseCase.execute({ email: this.email, password });
  }
  onCloseResult() {
    this.resultVisible = false;
  }
  async onResendPasswordLink() {
    this.generalError = '';
    this.loading = true;

    const result = await this.forgotPasswordUseCase.execute(this.email);

    this.loading = false;

    if (result.isErr()) {
      this.modalMessage =
        result.getErr()?.message ||
        'Неуспешно изпращане на линк за нулиране на парола. Моля опитайте отново.';
      this.modalSeverity = 'danger';
      this.resultVisible = true;
      return;
    }

    this.modalMessage = 'Нов линк за нулиране на парола беше изпратен успешно!';
    this.modalSeverity = 'success';
    this.resultVisible = true;
  }
}
